(ns app.front.view.property.gis-import.session-list
  (:require [app.domain.municipality.model :as municipality-model]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.select :as ui-select]
            [app.front.ui.table :as ui-table]
            [app.front.view.property.dashboard.subs :as property-dashboard-subs]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.href :as href]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]))

(rf/reg-event-fx ::init
  (fn [_ _]
    {:fx [[:dispatch [::fetch-creation-data]]
          [:dispatch [::fetch-existing-sessions]]]}))

(rf/reg-event-fx ::fetch-creation-data
  (fn [_ _]
    {:comm/command {:command    :property-management/creation-data
                    :on-success [::creation-data.success]
                    :method     :get}}))

(rf/reg-event-fx ::creation-data.success
  (fn [{:keys [db]} [_ _ creation-data]]
    {:db (assoc-in db [::property-dashboard-subs/initial-data :authorized-dgs] creation-data)}))

(rf/reg-event-fx ::fetch-existing-sessions
  (fn [_ _]
    {:comm/command {:command    :property-import/import-sessions
                    :on-success [::entities/merge-entity-db]
                    :method     :get}}))

(rf/reg-event-fx ::fetch-all-records
  (fn [_ [_ group-id municipality-id import-subtype]]
    {:fx [[:dispatch [:comm/command-monitored
                      {:command    :property-import/start-full-gis-fetch
                       :data       (util/mmap {:property-import-session/subtype import-subtype}
                                              group-id municipality-id)
                       :on-success [::entities/apply-changes]
                       :notify     {:success :property-gis-import/gis-fetch-started}}]]]}))

(rf/reg-event-db ::set-domain-group
  (rf/path [::state])
  (fn [db [_ domain-group]]
    (-> (dissoc db ::municipality)
        (assoc ::domain-group domain-group))))

(rf/reg-event-db ::set-municipality
  (rf/path [::state])
  (fn [db [_ municipality]]
    (assoc db ::municipality municipality)))

(rf/reg-sub ::state :-> ::state)

(rf/reg-sub ::gis-domain-groups
  :<- [::property-dashboard-subs/domain-groups]
  (fn [domain-groups _]
    (->> domain-groups
         (filter :remote-gis-available?))))

(rf/reg-sub ::domain-group
  :<- [::state]
  :<- [::gis-domain-groups]
  (fn [[state domain-groups] _]
    (::domain-group state (first domain-groups))))

(rf/reg-sub ::municipality
  :<- [::state]
  :<- [::domain-group]
  (fn [[state domain-group] _]
    (::municipality state (-> domain-group :municipalities first))))

(rf/reg-sub ::import-sessions
  :<- [::entities/context]
  (fn [ctx [_ subtype]]
    (when subtype
      (->> (entities/find-entities ctx {:property-import-session/subtype subtype})
           (sort-by :entity/created >)))))

(rf/reg-sub ::async-jobs
  (fn [[_ import-session-subtype]]
    [(rf/subscribe [::entities/context])
     (rf/subscribe [::import-sessions import-session-subtype])])
  (fn [[ctx sessions] _]
    (let [session-ids (->> sessions
                           (map :db/id)
                           set)]
      (->> (entities/find-entities ctx {:entity/type :entity.type/async-job})
           (filter #(->> % :entity/parent :db/id (contains? session-ids)))))))

(rf/reg-sub ::any-job-running?
  (fn [[_ import-session-subtype]]
    [(rf/subscribe [::async-jobs import-session-subtype])
     (rf/subscribe [:comm/command-processing? :property-import/start-full-gis-fetch])])
  (fn [[jobs cmd-running?] _]
    (boolean (or cmd-running?
                 (some (du/in-state-fn :async-job.state/running) jobs)))))


(rf/reg-sub ::back-href
  (fn [_]
    (href/href :view/property-dashboard)))


(rf/reg-sub ::view-data
  :<- [::back-href]
  (fn [back-href]
    {:back-href back-href}))


(defn- async-job-for-session [jobs session-id task-type]
  (->> jobs
       (filter #(and (-> % :entity/parent :db/id (= session-id))
                     (-> % :async-job/task (= task-type))))
       (sort-by :entity/created)
       last))

(defn- status+state [async-jobs {:db/keys [id]}]
  (let [{:domain/keys [state]} (->> [(async-job-for-session async-jobs id :async-job.task/property-gis-import)
                                     (async-job-for-session async-jobs id :async-job.task/property-gis-fetch)]
                                    (remove nil?)
                                    first)]
    (when state
      [(case state
         :async-job.state/running :warning
         :async-job.state/ready :ok
         :async-job.state/failed :critical
         nil)
       state])))

(defn import-sessions-table [{:keys [tr sessions lang import-subtype session-view]}]
  (let [format-date (<sub [::date-util/date-time-fn])
        async-jobs  (<sub [::async-jobs import-subtype])]
    [ui-table/modern-responsive-table
     {:tr         tr
      :items      sessions
      :id-func    :db/id
      :get-status #(-> (status+state async-jobs %) first)
      :link       {:description (tr [:open])
                   :make-href   #(href/href session-view
                                            {:session-id (:db/id %)})}
      :cols       (concat
                    [[:domain/state
                      #(-> (status+state async-jobs %) last vector tr)]
                     [:group/name
                      (comp :group/name :entity/parent)]
                     [:entity/created
                      (comp format-date :entity/created)]
                     [:municipalities/municipality
                      #(-> %
                           :property-import-session/municipality
                           (municipality-model/municipality-name lang))]
                     [:property-gis-import/gis-processed
                      #(-> (async-job-for-session async-jobs (:db/id %) :async-job.task/property-gis-fetch)
                           :async-job/processed-count)]
                     [:property-gis-import/gis-total
                      #(-> (async-job-for-session async-jobs (:db/id %) :async-job.task/property-gis-fetch)
                           :async-job/total-count)]]
                    (if (= import-subtype :property-import-session.subtype/initial)
                      [[:property-gis-import/import-processed
                        #(-> (async-job-for-session async-jobs (:db/id %) :async-job.task/property-gis-import)
                             :async-job/processed-count)]
                       [:property-gis-import/import-total
                        #(-> (async-job-for-session async-jobs (:db/id %) :async-job.task/property-gis-import)
                             :async-job/total-count)]]
                      [[:property-gis-import/update-processed
                        #(-> (async-job-for-session async-jobs (:db/id %) :async-job.task/property-attribute-gis-update)
                             :async-job/processed-count)]
                       [:property-gis-import/update-total
                        #(-> (async-job-for-session async-jobs (:db/id %) :async-job.task/property-attribute-gis-update)
                             :async-job/total-count)]]))}]))

(defn start-fetch-controls [{:keys [tr lang import-subtype]}]
  (let [selected-domain-group    (<sub [::domain-group])
        selected-municipality    (<sub [::municipality])
        multiple-municipalities? (-> selected-domain-group :municipalities count (> 1))
        job-running?             (<sub [::any-job-running? import-subtype])]
    [:section.p-8
     (if (seq (<sub [::gis-domain-groups]))
       [:div.flex.gap-8.items-end
        [:div
         (if (<sub [::property-dashboard-subs/multiple-domain-groups?])
           [ui-select/select {:id           "property-gis-import-domain-group-select"
                              :class        "full-width-select"
                              :allow-empty? false
                              :label-text   (tr [:property/select-domain-group])
                              :on-change    #(>evt [::set-domain-group %])
                              :required     true
                              :val->str     :group/name
                              :value        selected-domain-group
                              :values       (<sub [::gis-domain-groups])}]
           [:div
            [:div.bx--label (tr [:group/name])]
            [:div.flex.items-center {:style {:height "40px"}}
             (:group/name selected-domain-group)]])]
        [:div
         (if multiple-municipalities?
           [ui-select/select {:id           "property-gis-import-municipality-select"
                              :class        "full-width-select"
                              :allow-empty? false
                              :label-text   (tr [:municipalities/select-municipality])
                              :on-change    #(>evt [::set-municipality %])
                              :required     true
                              :val->str     #(municipality-model/municipality-name % lang)
                              :value        selected-municipality
                              :values       (<sub [::property-dashboard-subs/municipalities selected-domain-group])}]
           [:div
            [:div.bx--label (tr [:municipality])]
            [:div.flex.items-center {:style {:height "40px"}}
             (municipality-model/municipality-name selected-municipality lang)]])]
        [:div
         [ui/button
          {:kind     :primary
           :size     :field
           :disabled job-running?
           :on-click #(>evt [::fetch-all-records
                             (:db/id selected-domain-group)
                             (:db/id selected-municipality)
                             import-subtype])}
          (tr [:property-gis-import/fetch-from-gis])
          (when job-running?
            [:span.ml-4 (cc/inline-loading {:class-name "min-h-0"})])]]]
       [:div (tr [:property/no-available-domain-groups])])]))

(defn import-view [match]
  (let [tr       (<sub [:tr])
        lang     (<sub [:language])
        sessions (<sub [::import-sessions :property-import-session.subtype/initial])]
    [:div.cloudpermit-page
     [ui/page-header (util/mmap match)
      [:h1 (tr [:property-gis-import/main-heading])]]
     [start-fetch-controls (util/mmap {:import-subtype :property-import-session.subtype/initial}
                                      tr lang)]
     (when (seq sessions)
       [:section.p-8.mt-8
        [:h3 (tr [:property-gis-import/sessions])]
        [import-sessions-table (util/mmap {:import-subtype :property-import-session.subtype/initial
                                           :session-view   :view/property-gis-import-session}
                                          tr lang sessions)]])]))

(defn update-view [match]
  (let [tr       (<sub [:tr])
        lang     (<sub [:language])
        sessions (<sub [::import-sessions :property-import-session.subtype/attribute-update])]
    [:div.cloudpermit-page
     [ui/page-header (util/mmap match)
      [:h1 (tr [:property-gis-import/main-update-heading])]]
     [start-fetch-controls (util/mmap {:import-subtype :property-import-session.subtype/attribute-update}
                                      tr lang)]
     (when (seq sessions)
       [:section.p-8.mt-8
        [:h3 (tr [:property-gis-import/update-sessions])]
        [import-sessions-table (util/mmap {:import-subtype :property-import-session.subtype/attribute-update
                                           :session-view   :view/property-gis-update-session}
                                          tr lang sessions)]])]))

(def import-route
  {:name        :view/property-gis-import
   :view-sub    [::view-data]
   :view        import-view
   :controllers [{:start (fn [_]
                           (>evt [::init]))}]})

(def update-route
  {:name        :view/property-gis-update
   :view-sub    [::view-data]
   :view        update-view
   :controllers [{:start (fn [_]
                           (>evt [::init]))}]})
